import { Ajax } from '@/common'
import moment from 'moment'

export function pdfGenerating(item) {
  if (!item || item.status !== 'PENDING' || (item.filePath || '').trim()) return false
  if (item.pdfGenerationToken) return true
  if (item.pdfFailed || Number(item.pdfFailCount) >= 5) return false
  return Boolean(item.pdfGenerating || Number(item.pdfFailCount) > 0)
}

export function pdfEditable(item) {
  return Boolean(
    item &&
    (item.status === 'DRAFT' || item.status === 'PENDING') &&
    !pdfGenerating(item) &&
    !item.pdfGenerationToken
  )
}

export function canUndoJournal(item, user, allowed) {
  return Boolean(
    item &&
    item.status === 'PENDING' &&
    !pdfGenerating(item) &&
    allowed &&
    (String(user.userType) !== '1' || item.creatorId === user.userId)
  )
}

export function canRecallJournal(record) {
  if (!record || !record.timestamp) return false
  const hours = moment().diff(moment.utc(record.timestamp), 'hours')
  return hours >= 0 && hours <= 24
}

export function approvalErrorMessage(
  error,
  fallback = 'Approval request failed, please refresh and try again'
) {
  const response = error && error.response && error.response.data
  return (response && response.message) || (error && error.message) || fallback
}

export function pdfApproved(item) {
  return Boolean(
    item &&
    (item.pdfGenerating ||
      item.pdfFailed ||
      item.pdfGenerationToken ||
      Number(item.pdfFailCount) > 0)
  )
}

// Query epochs fence old-view polls; per-row versions overlay mutations onto
// in-flight list snapshots without cancelling a newer query or eating its page.
export default {
  data() {
    return {
      pdfListTimer: null,
      pdfListInFlight: false,
      pdfListDestroyed: false,
      pdfListRequestSeq: 0,
      pdfListQueryEpoch: 0,
      pdfListContextKey: '',
      pdfListReloading: false,
      pdfListMutations: {}
    }
  },
  watch: {
    list() {
      this.schedulePdfListPoll()
    }
  },
  beforeDestroy() {
    this.pdfListDestroyed = true
    clearTimeout(this.pdfListTimer)
  },
  methods: {
    canShowJournalUndo(item) {
      return canUndoJournal(item, this.user, this.hasPermission('Share journals_Edit'))
    },
    canShowJournalRecall(item) {
      return (
        item.status === 'POSTED' &&
        canRecallJournal(item.lastPost || item.lastApprove) &&
        this.hasPermission('Share journals_Full')
      )
    },
    showApprovalError(error) {
      if (this.$message) {
        this.$message.error(approvalErrorMessage(error))
      }
    },
    recordPdfListMutation(row) {
      const previous = this.pdfListMutations[row.id]
      this.$set(this.pdfListMutations, row.id, {
        version: previous ? previous.version + 1 : 1,
        row
      })
    },
    fetchPdfJournalList(data, append) {
      const { currentPage, ...context } = data
      const key = JSON.stringify(context)
      const contextChanged = Boolean(this.pdfListContextKey && this.pdfListContextKey !== key)
      if (contextChanged) {
        this.list = []
        this.pdfListMutations = {}
        this.pageNum = 1
        this.pages = 0
        append = false
      }
      this.pdfListContextKey = key
      if (!append) this.pdfListQueryEpoch++
      this.pdfListReloading = !append
      const epoch = this.pdfListQueryEpoch
      const requestSeq = ++this.pdfListRequestSeq
      const requestedPage = append ? this.pageNum + 1 : 1
      const versions = {}
      Object.keys(this.pdfListMutations).forEach((id) => {
        versions[id] = this.pdfListMutations[id].version
      })
      this.loading = true
      const current = () =>
        !this.pdfListDestroyed &&
        requestSeq === this.pdfListRequestSeq &&
        epoch === this.pdfListQueryEpoch
      return Ajax.get('journal/search/extraV1', { ...data, currentPage: requestedPage })
        .then((res) => {
          if (!current()) return
          if (res.code === '0000') {
            const rows = res.data.records.map((row) => {
              const local = this.pdfListMutations[row.id]
              return local && local.version > (versions[row.id] || 0) ? local.row : row
            })
            // A mutation during the fetch may also add a row absent from its snapshot.
            Object.keys(this.pdfListMutations).forEach((id) => {
              const local = this.pdfListMutations[id]
              if (
                local.version > (versions[id] || 0) &&
                !rows.some((row) => row.id === id) &&
                (!append || !this.list.some((row) => row.id === id))
              )
                rows.push(local.row)
            })
            const visible = rows.filter((row) => !data.status || row.status === data.status)
            if (!append) {
              const shell = document.getElementsByClassName('ggPage-main-center')[0]
              if (shell) shell.scrollTo({ top: 0 })
              this.list = visible
            } else {
              this.list.push(...visible)
            }
            this.pageNum = requestedPage
            this.pages = Math.round(res.data.count / res.data.size)
          }
        })
        .catch(() => {
          // Keep the current rows on transient list failures.
        })
        .finally(() => {
          if (current()) {
            this.loading = false
            this.pdfListReloading = false
            this.schedulePdfListPoll()
          }
        })
    },
    canShowJournalApprove(item) {
      return (
        item.status === 'PENDING' &&
        !pdfGenerating(item) &&
        !item.pdfGenerationToken &&
        this.hasPermission('Share journals_Full')
      )
    },
    canShowJournalDelete(item) {
      return (
        (item.status === 'DRAFT' || item.status === 'PENDING') &&
        !pdfGenerating(item) &&
        this.hasPermission('Manage journals_Full')
      )
    },
    schedulePdfListPoll() {
      if (
        this.pdfListDestroyed ||
        this.pdfListTimer ||
        this.pdfListInFlight ||
        this.pdfListReloading ||
        !this.list.some(pdfGenerating)
      )
        return
      this.pdfListTimer = setTimeout(() => {
        this.pdfListTimer = null
        this.pollPdfList()
      }, 10000)
    },
    async pollPdfList() {
      if (this.pdfListDestroyed || this.pdfListInFlight || this.pdfListReloading) return
      const epoch = this.pdfListQueryEpoch
      this.pdfListInFlight = true
      try {
        const rows = this.list.filter(pdfGenerating)
        await Promise.all(
          rows.map(async (row) => {
            try {
              const res = await Ajax.get('journal/' + row.id, { id: row.id })
              if (
                this.pdfListDestroyed ||
                epoch !== this.pdfListQueryEpoch ||
                !this.list.includes(row) ||
                !pdfGenerating(row) ||
                res.code !== '0000'
              )
                return null
              const fresh = res.data
              const ready = fresh.status === 'POSTED' && Boolean((fresh.filePath || '').trim())
              this.$set(row, 'status', fresh.status)
              this.$set(row, 'filePath', fresh.filePath || '')
              this.$set(row, 'pdfGenerationToken', fresh.pdfGenerationToken || '')
              this.$set(row, 'pdfFailCount', fresh.pdfFailCount || 0)
              this.$set(
                row,
                'pdfFailed',
                fresh.status === 'PENDING' &&
                  !fresh.pdfGenerationToken &&
                  !(fresh.filePath || '').trim() &&
                  Number(fresh.pdfFailCount) >= 5
              )
              this.$set(row, 'pdfGenerating', false)
              this.$set(row, 'pdfGenerating', pdfGenerating(row))
              this.recordPdfListMutation(row)
              if (ready) this.showPdfPosted(row)
              if (fresh.status !== 'PENDING') {
                if (this.status === 'PENDING') this.list = this.list.filter((i) => i !== row)
                if ('classInt' in this) this.classInt = row.id + Date.now()
              }
            } catch (e) {
              // Transient detail failures retry in the next serial batch.
            }
            return null
          })
        )
      } finally {
        this.pdfListInFlight = false
        this.schedulePdfListPoll()
      }
    },
    showPdfPosted(row) {
      this.loadingShow = true
      this.loadingText = (row.newsletter ? 'Newsletter' : 'Portfolio') + ' has been posted'
    },
    startApprovedPdf(id, type) {
      const row = this.list.find((i) => i.id === id)
      this.loadBtn = false
      if (!this.pdfListReloading) this.loading = false
      if (!row) {
        this.getJournal()
        return
      }
      // Only an explicit manual-upload flag permits immediate publication.
      if (row.isPdf === true) {
        const posted = { ...row, status: 'POSTED' }
        this.$set(this.list, this.list.indexOf(row), posted)
        this.recordPdfListMutation(posted)
        this.showPdfPosted(posted)
        if (this.status === 'PENDING') this.list = this.list.filter((i) => i !== posted)
        if ('classInt' in this) this.classInt = id + Date.now()
        return
      }
      const generating = { ...row, pdfFailCount: 0, pdfFailed: false, pdfGenerating: true }
      this.$set(this.list, this.list.indexOf(row), generating)
      this.recordPdfListMutation(generating)
      this.loadingShow = true
      this.loadingText = (type === 0 ? 'Newsletter' : 'Portfolio') + ' posting in progress'
      this.schedulePdfListPoll()
    },
    invalidatePdfListRow(id) {
      const index = this.list.findIndex((i) => i.id === id)
      if (index >= 0) {
        const draft = {
          ...this.list[index],
          status: 'DRAFT',
          pdfGenerating: false,
          pdfFailed: false,
          pdfGenerationToken: '',
          pdfFailCount: 0
        }
        this.$set(this.list, index, draft)
        this.recordPdfListMutation(draft)
      }
    }
  }
}
